import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getToken, signOut, useSession } from "@/auth/auth.client";

// Subset of the better-auth user
interface User {
  email: string;
  id: string;
  name: string;
}

interface LoginContextType {
  user: User | undefined;
  jwt: string | undefined;
  sessionToken: string | undefined;
  isAuthenticated: boolean;
  logout: () => void;
}

const LoginContext = createContext<LoginContextType | undefined>(undefined);

export function LoginProvider({ children }: { children: ReactNode }) {
  // Add jwt token to login context for auth using zero client
  const [jwt, setJwt] = useState<string | undefined>(undefined);
  const { data: session } = useSession();

  useEffect(() => {
    if (!session?.user?.id) return;
    // TODO: This is a separate request from the get-session request, there might be a way to add the token
    // into get get-session request using `customSession` plugin
    getToken().then(({ data }) => {
      if (data) {
        setJwt(data.token);
      }
    });
  }, [session?.user?.id]);

  const user = useMemo(() => {
    return session
      ? {
          email: session.user.email,
          id: session.user.id,
          name: session.user.name,
        }
      : undefined;
  }, [session]);

  const sessionToken = session?.session.token;

  const context = useMemo<LoginContextType>(
    () => ({
      user,
      jwt,
      sessionToken,
      isAuthenticated: !!(user && jwt && sessionToken),
      logout: () => signOut(),
    }),
    [user, jwt, sessionToken],
  );

  return (
    <LoginContext.Provider value={context}>{children}</LoginContext.Provider>
  );
}

export function useLogin() {
  const context = useContext(LoginContext);
  if (context === undefined) {
    throw new Error("useLogin must be used within a LoginProvider");
  }
  return context;
}
